import { useEffect, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useUnit } from 'effector-react';
import { $user, setUser, type User } from 'models/user';

const firstAttribute = (value: unknown) => {
  if (Array.isArray(value)) return typeof value[0] === 'string' ? value[0] : undefined;
  return typeof value === 'string' ? value : undefined;
};

const useUserProfile = (onSuccess?: () => void) => {
  const { keycloak } = useKeycloak();
  const user = useUnit($user);
  const loadedSubjectRef = useRef<string | null>(null);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  useEffect(() => {
    if (!keycloak.authenticated || !keycloak.tokenParsed) {
      loadedSubjectRef.current = null;
      return;
    }

    const token = keycloak.tokenParsed as Record<string, unknown>;
    const subject = keycloak.subject || String(token.sub || 'authenticated-user');
    if (loadedSubjectRef.current === subject) return;
    loadedSubjectRef.current = subject;

    let cancelled = false;
    keycloak
      .loadUserProfile()
      .then((profile) => {
        if (cancelled) return;
        const attributes = profile.attributes as Record<string, unknown> | undefined;
        setUser({
          ...token,
          groups: Array.isArray(token.groups) ? (token.groups as string[]) : [],
          email: profile.email || (token.email as string | undefined),
          firstname: profile.firstName || (token.given_name as string | undefined),
          secondname: profile.lastName || (token.family_name as string | undefined),
          phone:
            firstAttribute(attributes?.phoneNumber) || firstAttribute(token.phoneNumber) || null,
          num: firstAttribute(attributes?.num) || (token.num as string | undefined) || null,
          preferred_username: profile.username || token.preferred_username,
        } as User);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) onSuccessRef.current?.();
      });

    return () => {
      cancelled = true;
    };
  }, [keycloak, keycloak.authenticated, keycloak.subject, keycloak.tokenParsed]);

  return { user, keycloak };
};

export default useUserProfile;
